class PredictiveSearch extends SearchForm {
  constructor () {
    super()
    this.cachedResults = {}
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]')
    this.predictiveSearchContainer = this.querySelector('#predictive-search-container')
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search')
    this.isOpen = false
    this.abortController = new AbortController()
    this.searchTerm = ''

    this.setupEventListeners()
  }

  connectedCallback () {
    this.details = this.closest('details')

    if (!this.details || this.boundOnDetailsToggle) return

    this.boundOnDetailsToggle = this.onDetailsToggle.bind(this)
    this.details.addEventListener('toggle', this.boundOnDetailsToggle)
  }

  disconnectedCallback () {
    this.details?.removeEventListener('toggle', this.boundOnDetailsToggle)
    this.boundOnDetailsToggle = null
    this.details = null
  }

  setupEventListeners () {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this))

    this.input.addEventListener('focus', this.onFocus.bind(this))
    this.addEventListener('focusout', this.onFocusOut.bind(this))
    this.addEventListener('keyup', this.onKeyup.bind(this))
    this.addEventListener('keydown', this.onKeydown.bind(this))
  }

  getQuery () {
    return this.input.value.trim()
  }

  onChange () {
    super.onChange()
    const newSearchTerm = this.getQuery()

    if (!this.searchTerm) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove()
    }

    this.searchTerm = newSearchTerm

    if (this.searchTerm.length) {
      this.predictiveSearchContainer.classList.remove('empty')
      this.getSearchResults(this.searchTerm)
    } else {
      this.predictiveSearchContainer.classList.add('empty')
    }
  }

  onFormSubmit (event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault()
  }

  onFormReset (event) {
    super.onFormReset(event)
    if (super.shouldResetForm()) {
      this.searchTerm = ''
      this.abortController.abort()
      this.abortController = new AbortController()
      this.closeResults(true)

      if (this.details?.hasAttribute('open')) this.showEmptyState()
    }
  }

  onFocus () {
    const currentSearchTerm = this.getQuery()

    if (!currentSearchTerm.length) {
      this.open()
      this.predictiveSearchContainer.classList.add('empty')
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange()
    } else if (this.getAttribute('results') === 'true' && !this.isOpen) {
      this.open()
    } else if (currentSearchTerm.length) {
      this.getSearchResults(this.searchTerm)
    }
  }

  onFocusOut () {
    setTimeout(() => {
      if (this.details?.hasAttribute('open')) return
      if (!this.contains(document.activeElement)) this.close()
    })
  }

  onDetailsToggle () {
    if (this.details.hasAttribute('open')) {
      this.syncWithModal()
      return
    }

    this.close(false)
  }

  syncWithModal () {
    const term = this.getQuery()

    if (!term.length) {
      this.showEmptyState()
      return
    }

    this.open()

    if (this.searchTerm !== term || this.getAttribute('results') !== 'true') {
      this.getSearchResults(term)
      return
    }

    this.predictiveSearchContainer.classList.remove('empty')
  }

  showEmptyState () {
    this.predictiveSearchContainer.classList.add('empty')
    this.open()
  }

  onKeyup (event) {
    // if (!this.getQuery().length) this.close(true);
    event.preventDefault()

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break
      case 'ArrowDown':
        this.switchOption('down')
        break
      case 'Enter':
        this.selectOption()
        break
    }
  }

  onKeydown (event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault()
    }
  }

  switchOption (direction) {
    if (!this.getAttribute('open')) return

    const moveUp = direction === 'up'
    const selectedElement = this.querySelector('[aria-selected="true"]')

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null,
    )
    let activeElementIndex = 0

    if (moveUp && !selectedElement) return

    let selectedElementIndex = -1
    let i = 0

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i
      }
      i++
    }

    this.statusElement.textContent = ''

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1
    }

    if (activeElementIndex === selectedElementIndex) return

    const activeElement = allVisibleElements[activeElementIndex]

    activeElement.setAttribute('aria-selected', true)
    if (selectedElement) selectedElement.setAttribute('aria-selected', false)

    this.input.setAttribute('aria-activedescendant', activeElement.id)
  }

  selectOption () {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')

    if (selectedOption) selectedOption.click()
  }

  getSearchResults (searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()
    this.setLiveRegionLoadingState()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    const searchUrl = window.routes?.predictiveSearchUrl || window.routes?.predictive_search_url

    if (!searchUrl) {
      this.close()
      return
    }

    fetch(`${searchUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,query,collection&resources[limit]=4&resources[limit_scope]=each`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          this.close()
          throw error
        }

        return response.text()
      })
      .then((text) => {
        const parsedDoc = new DOMParser().parseFromString(text, 'text/html')
        const section = parsedDoc.querySelector('#shopify-section-predictive-search')
        const resultsMarkup = section?.innerHTML
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup
        })
        this.renderSearchResults(resultsMarkup)
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return
        }
        this.close()
        throw error
      })
  }

  setLiveRegionLoadingState () {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status')
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text')

    this.setLiveRegionText(this.loadingText)
    this.predictiveSearchResults.setAttribute('loading', true)
  }

  setLiveRegionText (statusText) {
    if (!this.statusElement) return

    this.statusElement.setAttribute('aria-hidden', 'false')
    this.statusElement.textContent = statusText

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true')
    }, 1000)
  }

  renderSearchResults (resultsMarkup) {
    this.open()
    this.predictiveSearchContainer.classList.remove('empty')
    this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', true)

    this.setLiveRegionResults()
  }

  setLiveRegionResults () {
    this.predictiveSearchResults.removeAttribute('loading')
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]')
    if (countElement) {
      this.setLiveRegionText(countElement.textContent)
    }
  }

  getResultsMaxHeight () {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom
    return this.resultsMaxHeight
  }

  open () {
    document.querySelectorAll('.mega-menu').forEach((menu) => {
      menu.removeAttribute('open')
    })

    this.setAttribute('open', true)
    this.input.setAttribute('aria-expanded', true)
    this.isOpen = true
  }

  close (clearSearchTerm = false) {
    this.closeResults(clearSearchTerm)
    this.isOpen = false
  }

  closeResults (clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = ''
      this.removeAttribute('results')
    }
    const selected = this.querySelector('[aria-selected="true"]')

    if (selected) selected.setAttribute('aria-selected', false)

    this.input.setAttribute('aria-activedescendant', '')
    this.removeAttribute('loading')
    this.removeAttribute('open')
    this.input.setAttribute('aria-expanded', false)
    this.resultsMaxHeight = false
    this.predictiveSearchContainer.classList.add('empty')
    this.predictiveSearchContainer.removeAttribute('style')
  }
}

customElements.define('predictive-search', PredictiveSearch)
